import { Component, OnInit, ViewChild } from '@angular/core';
import {
  PoBreadcrumb,
  PoModalComponent,
  PoNotificationService,
  PoPageAction,
  PoPageListComponent,
  PoSelectOption,
  PoTableAction,
  PoTableColumn,
  PoTableColumnSpacing
} from '@po-ui/ng-components';
import {
  ConvencaoSindicalDocumentoDto,
  ConvencoesSindicaisService
} from './convencoes-sindicais.service';
import {
  ESTADOS_POR_UF,
  FILTRO_CIDADE_ABC,
  FILTRO_CIDADE_ABC_LABEL,
  SindicalTagBadge,
  descreverEstado,
  ehFiltroCidadeAbc,
  estiloBadgeSindicalNgStyle,
  extrairCidades,
  extrairUf,
  montarBadgesSindicais
} from './convencoes-sindicais-tags';

interface ConvencaoSindicalTableRow extends ConvencaoSindicalDocumentoDto {
  estado: string;
  sizeFormatted: string;
}

@Component({
  selector: 'app-convencoes-sindicais',
  templateUrl: './convencoes-sindicais.component.html',
  styleUrl: './convencoes-sindicais.component.css',
  standalone: false
})
export class ConvencoesSindicaisComponent implements OnInit {
  @ViewChild('pageListComponent', { static: true }) pageListComponent!: PoPageListComponent;
  @ViewChild('modalRelatorio') modalRelatorio!: PoModalComponent;

  items: ConvencaoSindicalTableRow[] = [];
  isHideLoading = true;
  isGeneratingReport = false;
  moreItens = false;
  page = 1;
  pageSize = 20;
  filtroUf = '';
  filtroCidade = '';
  cidadeOptions: Array<PoSelectOption> = [];
  poTableColumnSpacing: PoTableColumnSpacing = PoTableColumnSpacing.Small;

  readonly estadoOptions: Array<PoSelectOption> = Object.entries(ESTADOS_POR_UF)
    .map(([uf, nome]) => ({ label: `${uf} - ${nome}`, value: uf }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));

  /**
   * Cidades já vistas nos documentos, agrupadas por UF. A API não expõe as tags existentes,
   * então as opções do filtro de cidade crescem conforme as páginas são lidas.
   */
  private readonly cidadesPorUf = new Map<string, Set<string>>();

  readonly breadcrumb: PoBreadcrumb = {
    items: [
      { label: 'Home', link: '/home' },
      { label: 'Relatórios' },
      { label: 'Convenções Sindicais' }
    ]
  };

  readonly pageActions: Array<PoPageAction> = [
    {
      label: 'Atualizar',
      action: () => this.loadMore(true),
      icon: 'an an-arrows-clockwise'
    },
    {
      label: 'Relatório',
      action: () => this.abrirModalRelatorio(),
      icon: 'an an-file-arrow-down'
    }
  ];

  readonly columns: Array<PoTableColumn> = [
    { property: 'estado', label: 'Estado', width: '180px' },
    { property: 'tags', label: 'Abrangência', type: 'columnTemplate' },
    {
      property: 'uploadedAt',
      label: 'Enviado em',
      type: 'date',
      format: 'dd/MM/yyyy HH:mm',
      width: '160px'
    },
    { property: 'contentType', label: 'Tipo do arquivo', width: '150px' },
    { property: 'fileName', label: 'Nome do arquivo' },
    { property: 'sizeFormatted', label: 'Tamanho', width: '110px' }
  ];

  readonly tableActions: Array<PoTableAction> = [
    {
      label: 'Visualizar arquivo',
      action: this.viewFile.bind(this),
      icon: 'an an-eye'
    },
    // Mantém o padrão de menu de três pontos mesmo quando há somente uma ação.
    {
      label: '',
      action: () => undefined,
      visible: () => false
    }
  ];

  constructor(
    private readonly convencoesSindicaisService: ConvencoesSindicaisService,
    private readonly poNotificationService: PoNotificationService
  ) {}

  ngOnInit(): void {
    this.loadMore(true);
  }

  get temFiltroAtivo(): boolean {
    return !!this.filtroUf || !!this.filtroCidade;
  }

  get podeGerarRelatorio(): boolean {
    return !!this.filtroUf && !this.isGeneratingReport;
  }

  get estadoSelecionadoDescricao(): string {
    if (!this.filtroUf) {
      return 'Todos os estados';
    }

    const nome = ESTADOS_POR_UF[this.filtroUf];
    return nome ? `${this.filtroUf} - ${nome}` : this.filtroUf;
  }

  get cidadeSelecionadaDescricao(): string {
    if (ehFiltroCidadeAbc(this.filtroCidade)) {
      return FILTRO_CIDADE_ABC_LABEL;
    }

    return this.filtroCidade || 'Todas as cidades';
  }

  onChangeFiltroUf(uf?: string): void {
    this.filtroUf = (uf ?? '').toUpperCase();
    this.filtroCidade = '';
    this.atualizarCidadeOptions();
    this.loadMore(true);
  }

  onChangeFiltroCidade(cidade?: string): void {
    this.filtroCidade = cidade ?? '';
    this.loadMore(true);
  }

  limparFiltros(): void {
    if (!this.temFiltroAtivo) {
      return;
    }

    this.filtroUf = '';
    this.filtroCidade = '';
    this.atualizarCidadeOptions();
    this.loadMore(true);
  }

  abrirModalRelatorio(): void {
    if (!this.filtroUf) {
      this.poNotificationService.warning(
        'Selecione um estado para gerar o relatório de convenções sindicais.'
      );
      return;
    }

    this.modalRelatorio.open();
  }

  tagsParaBadges(tags: string[] | null | undefined): SindicalTagBadge[] {
    return montarBadgesSindicais(tags);
  }

  estiloBadge(badge: SindicalTagBadge): Record<string, string> {
    return estiloBadgeSindicalNgStyle(badge.estilo);
  }

  loadMore(reset = false): void {
    this.isHideLoading = false;

    if (reset) {
      this.page = 1;
      this.items = [];
    }

    this.convencoesSindicaisService
      .getList(this.page, this.pageSize, undefined, this.filtrosDaConsulta())
      .subscribe({
        next: (data) => {
          const mapped = (data.items || []).map((item: ConvencaoSindicalDocumentoDto) => {
            this.registrarCidades(item);

            return {
              ...item,
              estado: this.getEstado(item),
              sizeFormatted: this.formatSize(item.sizeBytes)
            };
          });

          this.items = [...this.items, ...mapped];
          this.moreItens = !!data.hasNext;
          this.page++;
          this.atualizarCidadeOptions();
          this.isHideLoading = true;
        },
        error: () => {
          this.isHideLoading = true;
          this.poNotificationService.error('Não foi possível carregar as convenções sindicais.');
        }
      });
  }

  gerarRelatorio(): void {
    if (!this.filtroUf) {
      this.poNotificationService.warning(
        'Selecione um estado para gerar o relatório de convenções sindicais.'
      );
      return;
    }

    const uf = this.filtroUf;
    const cidadeRelatorio = ehFiltroCidadeAbc(this.filtroCidade)
      ? 'ABC'
      : this.filtroCidade || undefined;
    const abrangencia = this.filtroCidade
      ? `${this.estadoSelecionadoDescricao} / ${this.cidadeSelecionadaDescricao}`
      : this.estadoSelecionadoDescricao;

    this.isGeneratingReport = true;
    this.convencoesSindicaisService.gerarRelatorio(uf, cidadeRelatorio).subscribe({
      next: (blob) => {
        this.isGeneratingReport = false;
        this.modalRelatorio.close();
        const sufixo = cidadeRelatorio ? `${uf}-${cidadeRelatorio}` : uf;
        this.downloadBlob(blob, `convencoes-sindicais-${sufixo}.zip`);
        this.poNotificationService.success(
          `Pasta com as convenções sindicais de ${abrangencia} gerada com sucesso.`
        );
      },
      error: async (err) => {
        this.isGeneratingReport = false;
        this.modalRelatorio.close();
        const message = await this.extractErrorMessage(err);
        this.poNotificationService.error(message || 'Não foi possível gerar o relatório.');
      }
    });
  }

  viewFile(item: ConvencaoSindicalTableRow): void {
    this.convencoesSindicaisService.getFile(item.id).subscribe({
      next: (file) => {
        const fileUrl = URL.createObjectURL(file);
        const openedWindow = window.open(fileUrl, '_blank');

        if (!openedWindow) {
          URL.revokeObjectURL(fileUrl);
          this.poNotificationService.warning(
            'O navegador bloqueou a abertura do arquivo. Libere pop-ups e tente novamente.'
          );
          return;
        }

        setTimeout(() => URL.revokeObjectURL(fileUrl), 60_000);
      },
      error: () =>
        this.poNotificationService.error('Não foi possível abrir o arquivo selecionado.')
    });
  }

  private filtrosDaConsulta(): { uf?: string; cidade?: string } {
    return {
      uf: this.filtroUf || undefined,
      cidade: this.filtroCidade || undefined
    };
  }

  private registrarCidades(item: ConvencaoSindicalDocumentoDto): void {
    const uf = this.resolverUfDocumento(item);
    if (!uf) {
      return;
    }

    let cidades = this.cidadesPorUf.get(uf);
    if (!cidades) {
      cidades = new Set<string>();
      this.cidadesPorUf.set(uf, cidades);
    }

    for (const cidade of extrairCidades(item.tags)) {
      cidades.add(cidade);
    }
  }

  private atualizarCidadeOptions(): void {
    if (!this.filtroUf) {
      this.cidadeOptions = [];
      return;
    }

    const cidades = this.cidadesPorUf.get(this.filtroUf) ?? new Set<string>();
    const options = [...cidades]
      .sort((a, b) => a.localeCompare(b, 'pt-BR'))
      .map((cidade) => ({ label: cidade, value: cidade }));

    if (this.filtroUf === 'SP') {
      this.cidadeOptions = [
        { label: FILTRO_CIDADE_ABC_LABEL, value: FILTRO_CIDADE_ABC },
        ...options
      ];
      return;
    }

    this.cidadeOptions = options;
  }

  private resolverUfDocumento(item: ConvencaoSindicalDocumentoDto): string | null {
    const ufTag = extrairUf(item.tags);
    if (ufTag) {
      return ufTag;
    }

    const ufArquivo = item.fileName?.substring(0, 2).toUpperCase();
    return ufArquivo && ESTADOS_POR_UF[ufArquivo] ? ufArquivo : null;
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  private async extractErrorMessage(err: any): Promise<string | null> {
    try {
      const blob = err?.error;
      if (blob instanceof Blob) {
        const text = await blob.text();
        const json = JSON.parse(text);
        return json?.error || json?.title || null;
      }
      return err?.error?.error || err?.error?.title || null;
    } catch {
      return null;
    }
  }

  private formatSize(sizeBytes: number | null | undefined): string {
    if (sizeBytes == null || sizeBytes < 0) {
      return '-';
    }

    if (sizeBytes < 1024) {
      return `${sizeBytes} B`;
    }

    if (sizeBytes < 1024 * 1024) {
      return `${(sizeBytes / 1024).toFixed(1)} KB`;
    }

    return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private getEstado(item: ConvencaoSindicalDocumentoDto): string {
    // Documentos anteriores às tags só têm a UF no início do nome do arquivo.
    return descreverEstado(item.tags) ?? this.getEstadoPeloNomeArquivo(item.fileName);
  }

  private getEstadoPeloNomeArquivo(fileName: string | null | undefined): string {
    const uf = fileName?.substring(0, 2).toUpperCase();
    return uf && ESTADOS_POR_UF[uf] ? `${uf} - ${ESTADOS_POR_UF[uf]}` : 'Não identificado';
  }
}
